import type { OfflineDb } from './db'
import {
  codecFor,
  deriveWrapKey,
  generateDataKey,
  randomSalt,
  rewrapDataKey,
  unwrapDataKey,
  wrapDataKey,
  type Codec,
} from './crypto'
import type { StoredIdentity, VaultRecord } from './types'

/**
 * The device credential vault (§9): who has enabled offline encryption on
 * THIS device, and the PIN-wrapped data key that unlocks each of them. This
 * is the source for quick user-switching — a switch is just "pick an enrolled
 * user, enter their PIN, unlock their data key". No PIN and no usable key are
 * ever stored; a forgotten PIN is unrecoverable by design.
 */

const PIN_MIN = 4
const PIN_MAX = 12
const ITERATIONS = 210_000

export interface UnlockResult {
  dataKey: CryptoKey
  codec: Codec
  record: VaultRecord
}

/** The identity to restore into the app on unlock (no secret). */
export function identityFromVault(record: VaultRecord): Omit<StoredIdentity, 'storedAt'> {
  return {
    userId: record.userId,
    companyId: record.companyId,
    email: record.email,
    firstName: record.firstName,
    lastName: record.lastName,
    role: record.role,
    permissions: record.permissions,
    ...(record.totpEnabled !== undefined ? { totpEnabled: record.totpEnabled } : {}),
  }
}

function assertPin(pin: string): void {
  if (!/^\d+$/.test(pin) || pin.length < PIN_MIN || pin.length > PIN_MAX) {
    throw new Error(`PIN must be ${PIN_MIN}–${PIN_MAX} digits.`)
  }
}

/** Users enrolled for offline encryption on this device (for the switcher). */
export async function listVaultUsers(db: OfflineDb): Promise<VaultRecord[]> {
  const rows = await db.vault.toArray()
  return rows.sort((a, b) => (b.lastUnlockedAt ?? b.createdAt).localeCompare(a.lastUnlockedAt ?? a.createdAt))
}

export async function getVaultUser(db: OfflineDb, userId: string): Promise<VaultRecord | undefined> {
  return db.vault.get(userId)
}

export async function isEnrolled(db: OfflineDb, userId: string): Promise<boolean> {
  return (await db.vault.get(userId)) !== undefined
}

/**
 * Enable offline encryption for a user on this device: mint a fresh data key,
 * wrap it under the new PIN, persist the vault record, and return the unlocked
 * codec/key so the caller can immediately start encrypting. The returned data
 * key is the extractable original — callers that need to persist it as
 * "currently unlocked" should discard it after wiring the codec.
 */
export async function enrollUser(
  db: OfflineDb,
  identity: Omit<StoredIdentity, 'storedAt'>,
  pin: string,
): Promise<UnlockResult> {
  assertPin(pin)
  const salt = randomSalt()
  const dataKey = await generateDataKey()
  const wrappedDataKey = await wrapDataKey(dataKey, await deriveWrapKey(pin, salt, ITERATIONS))
  const record: VaultRecord = {
    userId: identity.userId,
    companyId: identity.companyId,
    email: identity.email,
    firstName: identity.firstName,
    lastName: identity.lastName,
    role: identity.role,
    permissions: identity.permissions,
    ...(identity.totpEnabled !== undefined ? { totpEnabled: identity.totpEnabled } : {}),
    salt,
    iterations: ITERATIONS,
    wrappedDataKey,
    createdAt: new Date().toISOString(),
    lastUnlockedAt: new Date().toISOString(),
  }
  await db.vault.put(record)
  return { dataKey, codec: codecFor(dataKey), record }
}

/** Unlock an enrolled user's data key with their PIN. Throws on a wrong PIN. */
export async function unlockUser(db: OfflineDb, userId: string, pin: string): Promise<UnlockResult> {
  const record = await db.vault.get(userId)
  if (!record) throw new Error('This user has not enabled offline access on this device.')
  const wrapKey = await deriveWrapKey(pin, record.salt, record.iterations)
  const dataKey = await unwrapDataKey(record.wrappedDataKey, wrapKey) // throws on wrong PIN
  await db.vault.update(userId, { lastUnlockedAt: new Date().toISOString() })
  return { dataKey, codec: codecFor(dataKey), record }
}

/** Change a user's PIN by re-wrapping the same data key (no data re-encrypt). */
export async function changePin(
  db: OfflineDb,
  userId: string,
  oldPin: string,
  newPin: string,
): Promise<void> {
  assertPin(newPin)
  const record = await db.vault.get(userId)
  if (!record) throw new Error('This user has not enabled offline access on this device.')
  const newSalt = randomSalt()
  const rewrapped = await rewrapDataKey(
    record.wrappedDataKey,
    await deriveWrapKey(oldPin, record.salt, record.iterations),
    await deriveWrapKey(newPin, newSalt, ITERATIONS),
  ) // throws on wrong old PIN
  await db.vault.update(userId, { wrappedDataKey: rewrapped, salt: newSalt, iterations: ITERATIONS })
}

/**
 * Forget a user on this device: drop the vault record so they can no longer
 * quick-switch or unlock here. Their encrypted partition is left in place
 * unless `wipe` is set — with the wrapped key gone it is unreadable anyway,
 * but callers that also cleared the partition pass `wipe` to reclaim space.
 */
export async function removeVaultUser(db: OfflineDb, userId: string): Promise<void> {
  await db.vault.delete(userId)
}
